import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExternalLinkAlt, faTag } from '@fortawesome/free-solid-svg-icons';
import { faGraduationCap, faChalkboardTeacher, faChess, faChild, faCalendarAlt, faBook } from '@fortawesome/free-solid-svg-icons';
import { getApplicationBySlug, applications } from '../../data/applications';
import applicationsHeroImage from '../../assets/images/duyurular.jpg';
import './ApplicationDetail.css';

const ApplicationDetail = () => {
  const { slug } = useParams();
  const application = getApplicationBySlug(slug);

  if (!application) {
    return (
      <div className="application-detail">
        <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h1>Uygulama Bulunamadı</h1>
          <p>Aradığınız uygulama bulunamadı.</p>
          <Link to="/uygulamalar" className="btn btn-primary">
            Uygulamalara Dön
          </Link>
        </div>
      </div>
    );
  }

  const getIcon = (iconName) => {
    const icons = {
      academy: faGraduationCap,
      tutor: faChalkboardTeacher,
      ib: faBook,
      chess: faChess,
      kids: faChild,
      program: faCalendarAlt,
      edtech: faBook
    };
    return icons[iconName] || faGraduationCap;
  };

  // Find previous and next applications
  const currentIndex = applications.findIndex(a => a.slug === slug);
  const previousApplication = currentIndex > 0 ? applications[currentIndex - 1] : null;
  const nextApplication = currentIndex < applications.length - 1 ? applications[currentIndex + 1] : null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": application.title,
    "description": application.description,
    "url": application.url,
    "applicationCategory": application.category,
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "TRY"
    }
  };

  const faqData = application.faqs || [];

  const faqStructuredData = faqData.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="application-detail">
      <Helmet>
        <title>{application.title} | ProgenX Uygulamaları</title>
        <meta name="description" content={application.description} />
        <meta name="keywords" content={`progenx, ${application.tags.join(', ')}, ${application.category}, uygulama`} />
        <meta property="og:title" content={application.title} />
        <meta property="og:description" content={application.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://progenxacademy.com/uygulamalar/${application.slug}`} />
        <link rel="canonical" href={`https://progenxacademy.com/uygulamalar/${application.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        {faqStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData)}
          </script>
        )}
      </Helmet>
      
      <Hero 
        title={application.title} 
        backgroundImage={applicationsHeroImage}
      />
      
      <section className="application-detail-content">
        <div className="container">
          <Link to="/uygulamalar" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} />
            Tüm Uygulamalara Dön
          </Link>

          <article className="application-article">
            <div className="application-header">
              <div className="app-icon-large">
                <FontAwesomeIcon icon={getIcon(application.icon)} />
              </div>
              <div className="application-header-text">
                <div className="application-meta">
                  <span className="application-category">
                    <FontAwesomeIcon icon={faTag} /> {application.category}
                  </span>
                </div>
                <h1 className="application-title">{application.title}</h1>
                <p className="application-short-description">{application.shortDescription}</p>
              </div>
            </div>

            <div 
              className="application-content"
              dangerouslySetInnerHTML={{ __html: application.content }} 
            />

            <div className="application-cta">
              <a 
                href={application.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Uygulamaya Git
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </a>
            </div>

            {application.tags && application.tags.length > 0 && (
              <div className="application-tags">
                {application.tags.map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
            )}

            {faqData.length > 0 && (
              <section className="application-faq">
                <h2>Sıkça Sorulan Sorular</h2>
                {faqData.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </section>
            )}
          </article>

          <div className="application-navigation">
            {previousApplication && (
              <Link to={`/uygulamalar/${previousApplication.slug}`} className="nav-link prev">
                <FontAwesomeIcon icon={faArrowLeft} />
                <div>
                  <span className="nav-label">Önceki</span>
                  <span className="nav-title">{previousApplication.title}</span>
                </div>
              </Link>
            )}
            {nextApplication && (
              <Link to={`/uygulamalar/${nextApplication.slug}`} className="nav-link next">
                <div>
                  <span className="nav-label">Sonraki</span>
                  <span className="nav-title">{nextApplication.title}</span>
                </div>
                <FontAwesomeIcon icon={faArrowLeft} style={{ transform: 'rotate(180deg)' }} />
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplicationDetail;
